import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Box,
  Button,
  Spinner,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Avatar,
  Badge,
  Flex,
  useToast
} from '@chakra-ui/react';
import { formatNumber, handleUserClick } from '../../../../../../utils/helper.util';
import { commentApi } from '../../../../../../apis/comment.api';
import { useNavigate } from 'react-router-dom';
import VerifiedIcon from '../../../../../../assets/verified-icon.svg';
import FoundingCreatorIcon from '../../../../../../assets/founding_creator_icon.png';
import UserAvatar from '../../../../../../assets/medium_default_optimized.png';

// Import reaction icons
import HeartReactionIcon from '../../../../../../assets/heart-reaction-icon.svg';
import SadReactionIcon from '../../../../../../assets/sad-reaction-icon.svg';
import AngryReactionIcon from '../../../../../../assets/angry-reaction-icon.svg';
import FunnyReactionIcon from '../../../../../../assets/funny-reaction-icon.svg';
import GalaxyBrainReactionIcon from '../../../../../../assets/galaxy-brain-reaction-icon.svg';
import InsightfulReactionIcon from '../../../../../../assets/insightful-reaction-icon.svg';
import NeedsCitationIcon from '../../../../../../assets/needs-citation.svg';
import BadLogicIcon from '../../../../../../assets/puzzle.svg';

const REACTIONS = [
  { type: 'like', icon: HeartReactionIcon, textDisplay: 'Like', key: 'like' },
  { type: 'funny', icon: FunnyReactionIcon, textDisplay: 'Funny', key: 'funny' },
  { type: 'angry', icon: AngryReactionIcon, textDisplay: 'Angry', key: 'angry' },
  { type: 'sad', icon: SadReactionIcon, textDisplay: 'Sad', key: 'sad' },
  { type: 'innovative', icon: InsightfulReactionIcon, textDisplay: 'Insightful', key: 'innovative' },
  { type: 'big_brain', icon: GalaxyBrainReactionIcon, textDisplay: 'Big Brain', key: 'big_brain' },
  { type: 'need_source', icon: NeedsCitationIcon, textDisplay: 'Needs Source', key: 'need_source' },
  { type: 'poor_logic', icon: BadLogicIcon, textDisplay: 'Poor Logic', key: 'poor_logic' }
];

interface CommentReactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  commentId: string;
  totalReactions: number;
}

const CommentReactionModal: React.FC<CommentReactionModalProps> = ({
  isOpen,
  onClose,
  commentId,
  totalReactions
}) => {
  const [reactionData, setReactionData] = useState<Record<string, number>>({});
  const [reactionUsers, setReactionUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedReactionFilter, setSelectedReactionFilter] = useState<string>('all');
  const [usersPagination, setUsersPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
    hasNextPage: false
  });
  
  const navigate = useNavigate();
  const toast = useToast();

  const fetchReactionUsers = async (reactionType?: string, page: number = 1) => {
    try {
      setIsLoadingUsers(true);
      const response = await commentApi.getCommentReactionUsers(commentId, {
        reactionType: reactionType === 'all' ? undefined : reactionType,
        page,
        limit: 20
      });
      
      if (response.data) {
        if (page === 1) {
          setReactionUsers(response.data);
        } else {
          setReactionUsers(prev => [...prev, ...response.data]);
        }
        setUsersPagination(response.pagination);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load reaction users',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isOpen && commentId) {
      const fetchData = async () => {
        try {
          setIsLoading(true);
          setError(null);
          const [reactionBreakdown] = await Promise.all([
            commentApi.getCommentReactionBreakdown(commentId)
          ]);
          setReactionData(reactionBreakdown || {});
          
          // Fetch users for the initial view
          await fetchReactionUsers('all', 1);
        } catch (err) {
          setError('Failed to load reaction data');
        } finally {
          setIsLoading(false);
        }
      };

      fetchData();
    }
  }, [isOpen, commentId]);

  useEffect(() => {
    if (isOpen && commentId && selectedReactionFilter) {
      fetchReactionUsers(selectedReactionFilter, 1);
    }
  }, [selectedReactionFilter]);

  const getTopReactions = () => {
    return REACTIONS
      .filter((reaction) => (reactionData[reaction.key] || 0) > 0)
      .sort((a, b) => (reactionData[b.key] || 0) - (reactionData[a.key] || 0))
      .slice(0, 3);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Comment Reactions</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {isLoading ? (
            <VStack py={8}>
              <Spinner size="lg" color="green.500" />
              <Text color="gray.600">Loading reaction data...</Text>
            </VStack>
          ) : error ? (
            <VStack py={8}>
              <Text color="red.500">{error}</Text>
              <Button onClick={() => setIsLoading(true)} colorScheme="green" size="sm">
                Try again
              </Button>
            </VStack>
          ) : (
            <VStack spacing={6} align="stretch">
              {/* Summary Section */}
              <Box bg="green.50" p={4} borderRadius="lg" border="1px solid" borderColor="green.200">
                <Text fontWeight="bold" mb={3} color="green.800">
                  Reaction Summary
                </Text>
                
                <HStack mb={3}>
                  <Box bg="white" px={3} py={1} borderRadius="md" shadow="sm">
                    <Text fontWeight="medium">
                      Total Reactions: {formatNumber(totalReactions)}
                    </Text>
                  </Box>
                </HStack>

                {getTopReactions().length > 0 && (
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={2}>Top Reactions:</Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {getTopReactions().map((reaction) => (
                        <Box
                          key={reaction.type}
                          bg="white"
                          px={3}
                          py={2}
                          borderRadius="md"
                          shadow="sm"
                          borderLeft="3px solid"
                          borderLeftColor="green.500"
                        >
                          <HStack spacing={2}>
                            <img src={reaction.icon} alt={reaction.textDisplay} style={{ width: '16px', height: '16px' }} />
                            <Text fontWeight="medium" fontSize="sm">
                              {formatNumber(reactionData[reaction.key] || 0)}
                            </Text>
                            <Text color="gray.500" fontSize="sm">
                              {reaction.textDisplay}
                            </Text>
                          </HStack>
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                )}
              </Box>

              {/* Tabs for different views */}
              <Tabs index={selectedTab} onChange={setSelectedTab}>
                <TabList>
                  <Tab>Grid View</Tab>
                  <Tab>Chart View</Tab>
                  <Tab>Users</Tab>
                </TabList>

                <TabPanels>
                  {/* Grid View */}
                  <TabPanel px={0}>
                    <VStack spacing={4} align="stretch">
                      <Text fontWeight="medium" color="gray.800">
                        All Reactions
                      </Text>
                      
                      <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(120px, 1fr))" gap={3}>
                        {REACTIONS.map((reaction) => (
                          <Box
                            key={reaction.type}
                            p={4}
                            borderRadius="lg"
                            border="1px solid"
                            borderColor="gray.200"
                            textAlign="center"
                            _hover={{ shadow: 'md' }}
                          >
                            <Box
                              bg="white"
                              p={3}
                              borderRadius="full"
                              display="inline-block"
                              shadow="sm"
                              borderBottom="2px solid"
                              borderBottomColor="green.500"
                              mb={2}
                            >
                              <img 
                                src={reaction.icon} 
                                alt={reaction.textDisplay} 
                                style={{ width: '24px', height: '24px' }}
                              />
                            </Box>
                            <Text fontWeight="bold" fontSize="lg">
                              {formatNumber(reactionData[reaction.key] || 0)}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {reaction.textDisplay}
                            </Text>
                          </Box>
                        ))}
                      </Box>
                    </VStack>
                  </TabPanel>

                  {/* Chart View */}
                  <TabPanel px={0}>
                    <VStack spacing={4} align="stretch">
                      <Text fontWeight="medium" color="gray.800">
                        Reaction Distribution
                      </Text>
                      
                      <Box p={4} border="1px solid" borderColor="gray.200" borderRadius="lg">
                        <VStack spacing={3}>
                          {REACTIONS.map((reaction) => {
                            const value = reactionData[reaction.key] || 0;
                            const percentage = totalReactions ? Math.round((value / totalReactions) * 100) : 0;
                            
                            return (
                              <HStack key={reaction.type} w="full" spacing={3}>
                                <HStack minW="100px" spacing={2}>
                                  <img 
                                    src={reaction.icon} 
                                    alt={reaction.textDisplay}
                                    style={{ width: '16px', height: '16px' }}
                                  />
                                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                    {reaction.textDisplay}
                                  </Text>
                                </HStack>
                                
                                <Box flex={1} bg="gray.100" borderRadius="full" h={4} overflow="hidden">
                                  <Box
                                    h="full"
                                    bg="green.500"
                                    borderRadius="full"
                                    width={`${percentage}%`}
                                    transition="width 0.3s ease"
                                  />
                                </Box>
                                
                                <HStack minW="80px" justify="flex-end" spacing={1}>
                                  <Text fontWeight="medium" fontSize="sm">
                                    {value}
                                  </Text>
                                  <Text color="gray.500" fontSize="xs">
                                    ({percentage}%)
                                  </Text>
                                </HStack>
                              </HStack>
                            );
                          })}
                        </VStack>
                      </Box>
                    </VStack>
                  </TabPanel>

                  {/* Users View */}
                  <TabPanel px={0}>
                    <VStack spacing={4} align="stretch">
                      <Text fontWeight="medium" color="gray.800">
                        Users Who Reacted
                      </Text>

                      {/* Filter by reaction type */}
                      <Box>
                        <Text fontSize="sm" color="gray.600" mb={2}>Filter by reaction:</Text>
                        <HStack spacing={2} flexWrap="wrap">
                          <Button
                            size="sm"
                            variant={selectedReactionFilter === 'all' ? 'solid' : 'outline'}
                            colorScheme="green"
                            onClick={() => setSelectedReactionFilter('all')}
                          >
                            All ({formatNumber(totalReactions)})
                          </Button>
                          {REACTIONS.filter(r => (reactionData[r.key] || 0) > 0).map((reaction) => (
                            <Button
                              key={reaction.key}
                              size="sm"
                              variant={selectedReactionFilter === reaction.key ? 'solid' : 'outline'}
                              colorScheme="green"
                              leftIcon={<img src={reaction.icon} alt={reaction.textDisplay} style={{ width: '16px', height: '16px' }} />}
                              onClick={() => setSelectedReactionFilter(reaction.key)}
                            >
                              {formatNumber(reactionData[reaction.key] || 0)}
                            </Button>
                          ))}
                        </HStack>
                      </Box>

                      {/* Users list */}
                      <Box maxH="400px" overflowY="auto">
                        {isLoadingUsers && reactionUsers.length === 0 ? (
                          <VStack py={8}>
                            <Spinner size="lg" color="green.500" />
                            <Text color="gray.600">Loading users...</Text>
                          </VStack>
                        ) : reactionUsers.length === 0 ? (
                          <VStack py={8}>
                            <Text color="gray.500">No users found for this reaction</Text>
                          </VStack>
                        ) : (
                          <VStack spacing={3} align="stretch">
                            {reactionUsers.map((userReaction, index) => (
                              <Box
                                key={`${userReaction.user.id}-${index}`}
                                p={3}
                                borderRadius="md"
                                border="1px solid"
                                borderColor="gray.200"
                                _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                                onClick={(e) => handleUserClick(e, () => navigate(`/${userReaction.user.username}`))}
                              >
                                <HStack spacing={3}>
                                  <Avatar
                                    size="sm"
                                    src={userReaction.user.imgUrl || UserAvatar}
                                    name={userReaction.user.firstName}
                                  />
                                  <Box flex={1} minW={0}>
                                    <HStack spacing={2} align="center">
                                      <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                                        {userReaction.user.firstName}
                                      </Text>
                                      {userReaction.user.isVerifiedAuthor && (
                                        <img src={VerifiedIcon} alt="verified" style={{ width: '14px', height: '14px' }} />
                                      )}
                                      {userReaction.user.isFoundingCreator && (
                                        <img src={FoundingCreatorIcon} alt="Founding Creator" title="Founding Creator" style={{ width: '14px', height: '14px' }} />
                                      )}
                                      {userReaction.user.username && (
                                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                          @{userReaction.user.username}
                                        </Text>
                                      )}
                                    </HStack>
                                  </Box>
                                  <HStack spacing={2}>
                                    <img 
                                      src={REACTIONS.find(r => r.key === userReaction.reactionType)?.icon} 
                                      alt={userReaction.reactionTypeDisplay}
                                      style={{ width: '18px', height: '18px' }}
                                    />
                                    <Badge colorScheme="green" fontSize="xs">
                                      {userReaction.reactionTypeDisplay}
                                    </Badge>
                                  </HStack>
                                </HStack>
                              </Box>
                            ))}

                            {/* Load more button */}
                            {usersPagination.hasNextPage && (
                              <Button
                                variant="outline"
                                colorScheme="green"
                                onClick={() => fetchReactionUsers(selectedReactionFilter, usersPagination.page + 1)}
                                isLoading={isLoadingUsers}
                                loadingText="Loading more..."
                              >
                                Load More ({usersPagination.totalPages - usersPagination.page} pages remaining)
                              </Button>
                            )}
                          </VStack>
                        )}
                      </Box>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CommentReactionModal;
